<script setup lang="ts">
import type { Component, Document, Page } from '@bloomreach/spa-sdk'

import closeIcon from '@/assets/icons/blog/close.svg'

/**
 * SDX
 */

const props = defineProps<{
    component: Component
    page: Page
}>()

const { component, page } = toRefs(props)

const dataPage = page.value.toJSON()
const models = component?.value.getModels()

const refPage = useExtractPage(models?.document?.$ref)
const contentDataPage = refPage && dataPage?.page[refPage]
const {
    actionTitle,
    legalTitle,
    legalDescription,
    successContent,
    errorContent,
    title,
    consentsSection,
    inputs,
    description,
} = contentDataPage?.data

/** END SDX */
const isOpen = ref(false)
const typeMessage = ref<'success' | 'error' | null>(null)
const newsletterBtn = ref()
const showSendMessage = ref(false)

const messageSendForm = computed(() => {
    if (typeMessage.value === 'success') {
        return { ...successContent }
    }
    if (typeMessage.value === 'error') {
        return { ...errorContent }
    }
    return {}
})

function showModal() {
    isOpen.value = true
    document.body.style.overflow = 'hidden'
}

function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isOpen.value) {
        hideModal()
    }
}

function handleTypeMessage(type: string) {
    if (type === 'success' || type === 'error') {
        showSendMessage.value = true
        typeMessage.value = type
    }
}

function hideModal() {
    document.body.style.overflow = ''
    isOpen.value = false
    typeMessage.value = null
    showSendMessage.value = false
    if (newsletterBtn.value) {
        newsletterBtn.value.focus()
    }
}

watch(isOpen, (newValue) => {
    if (newValue) {
        document.addEventListener('keydown', handleKeyDown)
    } else {
        document.removeEventListener('keydown', handleKeyDown)
    }
})
const refAside = ref()
onMounted(() => {
    useFocusTrap(refAside.value)
})
</script>

<template>
    <div class="mx-auto mb-3 max-w-6.5xl px-3 md:mb-6 xl:mb-16 xl:px-0">
        <button
            ref="newsletterBtn"
            class="font-bold text-plush-primary underline"
            @click="showModal"
        >
            {{ title }}
        </button>
    </div>
    <!-- aside -->
    <aside
        ref="refAside"
        class="c-transition fixed left-0 top-0 z-[-1] h-screen w-screen bg-black/75 opacity-0"
        :class="{ 'z-[9999] opacity-100': isOpen }"
    >
        <div
            class="sidebar c-transition fixed right-0 top-0 h-screen w-full translate-x-full bg-white md:w-1/2 xl:w-[40vw]"
            :class="{ 'open c-transition': isOpen }"
        >
            <PlushBlogNewsletterSendMessage
                v-if="showSendMessage"
                :data="messageSendForm"
                :type="typeMessage"
                @close-message="hideModal"
            />
            <header class="relative flex h-[168px] items-end bg-plush-primary">
                <button
                    :tabindex="isOpen ? 0 : -1"
                    class="absolute right-2 top-2"
                    @click="hideModal()"
                >
                    <AtomImage :src="closeIcon" />
                </button>
                <h2
                    class="mx-6 mb-3 text-2md font-bold text-white md:mb-6 md:mr-0 xl:ml-12"
                    v-html="title"
                />
            </header>

            <div
                v-if="!showSendMessage"
                id="test"
                class="c-scrollbar px-6 pt-10 md:px-0 md:pl-6"
            >
                <PlushBlogNewsletterFormNewsletter
                    :tab-index="isOpen ? 0 : -1"
                    :action-title="actionTitle"
                    :legal-title="legalTitle"
                    :legal-description="legalDescription"
                    :consents-section="consentsSection"
                    :inputs="inputs"
                    :description="description"
                    @type-message="handleTypeMessage"
                />
            </div>
        </div>
    </aside>
</template>

<style lang="scss" scoped>
.c-scrollbar {
    max-height: calc(100vh - 168px);
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 8px; // Szerokość scrolla
    }
    &::-webkit-scrollbar-thumb {
        background-color: #00283d; //Kolor paska
        border-radius: 10px; // Zaokrąglenie paska
        border: 2px solid transparent; //odstęp
        background-clip: content-box; // pozwala na odstęp
    }
    &::-webkit-scrollbar-track {
        background-color: transparent; // kolor tła scrolla
    }
}
.c-transition {
    transition: 0.5s;
}
.open {
    transform: translateX(0);
}

aside {
    outline: none;
    & :focus,
    & :focus-visible,
    & :focus-within {
        @apply outline-plush-pink outline-2 outline-offset-4;
    }
}
</style>
